import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { BinContext } from '../../../../../context/BinContext';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { ContainerContext } from '../../../../../context/ContainerContext';
import { ContainerTypeContext } from '../../../../../context/ContainerTypeContext';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import { SystemBin } from '../../../../../types/bin/bin';
import { ContainerInput } from '../../../../../types/container';
import { CustomFieldEntityChildType, CustomFieldEntityType } from '../../../../../types/custom_field/customField';
import { t } from '../../../../../types/translation/Translator';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import LoadingPackage from '../../../Common/LoadingPackage';
import { SystemStockLocation } from '../../../../../types/stock_location/stockLocation';
import { ContainerTypeIdSearchSelect, DropdownSearchSelect } from '../../../../components/Dropdown';
import { TextInput } from '../../../../components/TextInput';
import { Checkbox } from '../../../../components/Checkbox';
import { Tooltip } from '../../../../components/Tooltip';
import { Icon } from '../../../../icons';
import { IconType } from '../../../../icons/Icon';
import { UserContext } from '../../../../../context/UserContext';
import { ContactContext } from '../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { StockLocationRoleAssignmentContext } from '../../../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../../../types/stock_location_role_assignment';
import { BinStatusContext } from '../../../../../context/BinStatusContext';

interface ContainerFormProps {
  input: ContainerInput;
  setInput: React.Dispatch<React.SetStateAction<ContainerInput>>;
}

export default function ContainerForm({ input, setInput }: ContainerFormProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { bins, binsLoading } = useContext(BinContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { containers, containersLoading } = useContext(ContainerContext);
  const { containerTypes, containerTypesLoading } = useContext(ContainerTypeContext);
  const { customFields, customFieldsLoading } = useContext(CustomFieldContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const { containerCustomFields, propagationContainerCustomFields, showCustomFields } = useMemo(() => {
    const containerCustomFields = [...customFields.values()].filter(
      cf => cf.entityType === CustomFieldEntityType.container && !cf.hidden,
    );
    const propagationContainerCustomFields = [...customFields.values()].filter(
      cf => cf.entityChildType === CustomFieldEntityChildType.container && !cf.hidden,
    );
    const showCustomFields = containerCustomFields.length > 0 || propagationContainerCustomFields.length > 0;

    return {
      containerCustomFields,
      propagationContainerCustomFields,
      showCustomFields,
    };
  }, [customFields]);

  if (binsLoading || containersLoading || containerTypesLoading || customFieldsLoading) {
    return (
      <div className='flex h-full items-center justify-center'>
        <LoadingPackage />
      </div>
    );
  }

  const disabled = !isAdmin;
  const isSupervisor = hasStockLocationRole(
    input.companyId,
    input.stockLocationId || '',
    StockLocationRole.STOCK_LOCATION_SUPERVISOR,
  );

  const isUpdate = !!input.id;
  const binIsVirtual = input.binId === SystemBin.id;
  const validContainerIds = binIsVirtual
    ? new Set<string>()
    : new Set([...containers.values()].filter(el => el.id != input.id && el.binId === input.binId).map(el => el.id));

  return (
    <div className='grid gap-8'>
      <div className='grid gap-4'>
        <p className='section-title'>General Properties</p>
        <div className='r-grid'>
          <TextInput
            mandatory
            disabled={disabled || !!input.externalIdentifier}
            label={t(TranslationKey.identifier).singular.label}
            placeholder={t(TranslationKey.identifier).singular.label}
            value={input.identifier}
            onChange={identifier => setInput(input => input.withIdentifier(identifier))}
          />
          <ContainerTypeIdSearchSelect
            value={input.containerTypeId}
            onSelect={id => setInput(input => input.withContainerTypeId(id))}
            items={[...containerTypes.values()].filter(c => c.companyId === currentCompany.id)}
          />
        </div>
        <Checkbox
          disabled={disabled || !!input.externalIdentifier}
          value={input.deleteOnOutbound}
          onChange={checked => setInput(input => input.withDeleteOnOutbound(checked))}
          label={t(TranslationKey.deleteOnOutbound).singular.upper}
          tooltip={t(TranslationKey.deleteOnOutboundContainerTooltip).singular.upper}
        />
      </div>

      {showCustomFields && (
        <div className='grid gap-4'>
          <p className='section-title'>Custom Fields</p>

          {containerCustomFields.length ? (
            <div className='r-grid'>
              {containerCustomFields.map(cf => (
                <CustomFieldInput
                  item={cf}
                  value={input.customFields.get(cf.id)?.value}
                  onChange={setInput}
                  entity={input}
                  users={companyUsers}
                  contacts={contacts}
                  disabled={!isSupervisor}
                  productMasterData={productMasterData}
                />
              ))}
            </div>
          ) : null}

          {propagationContainerCustomFields.length ? (
            <div className='r-grid'>
              {propagationContainerCustomFields.map(cf => (
                <CustomFieldInput
                  item={cf}
                  value={input.customFields.get(cf.id)?.value}
                  onChange={setInput}
                  entity={input}
                  disabled={true}
                  users={companyUsers}
                  contacts={contacts}
                  productMasterData={productMasterData}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}

      <div className='grid gap-4'>
        <div className='flex items-center gap-1'>
          <p className='section-title'>Container Location</p>
          {input.id && (
            <Tooltip content={'A containers location can only be updated through a transaction'}>
              <Icon type={IconType.alertCircle} stroke='stroke-current' size='size-4' />
            </Tooltip>
          )}
        </div>

        <div className='r-grid'>
          <DropdownSearchSelect
            label={t(TranslationKey.stockLocation).singular.label}
            disabled={isUpdate}
            placeholder={t(TranslationKey.selectStockLocation).singular.label}
            value={input.stockLocationId === SystemStockLocation.id ? '' : input.stockLocationId}
            items={[
              ...new Set(
                [...stockLocations.values()].filter(el => el.companyId === currentCompany.id).map(el => el.id),
              ),
            ]}
            textBuilder={item => stockLocations.get(item || '')?.name || ''}
            onSelect={item => setInput(input => input.withStockLocationId(item || undefined))}
          />
          <DropdownSearchSelect
            label={t(TranslationKey.bin).singular.label}
            mandatory={!!input.stockLocationId && input.stockLocationId !== SystemStockLocation.id}
            disabled={!input.stockLocationId || input.stockLocationId === SystemStockLocation.id || isUpdate}
            placeholder={t(TranslationKey.selectBin).singular.label}
            value={binIsVirtual ? undefined : input.binId}
            items={[
              ...new Set(
                [...bins.values()]
                  .filter(el => {
                    if (el.companyId !== currentCompany.id) return false;
                    if (el.stockLocationId !== input.stockLocationId) return false;
                    const binStatus = binStatuses.get(el.binStatusId || '');
                    if (binStatus && !binStatus.inboundAllowed) return false;
                    return true;
                  })
                  .map(el => el.id),
              ),
            ]}
            textBuilder={item => bins.get(item || '')?.name || ''}
            onSelect={selected => setInput(input => input.withBinId(selected || ''))}
          />
          {validContainerIds.size ? (
            <Grid item xs={12}>
              <DropdownSearchSelect
                label={t(TranslationKey.container).singular.label}
                disabled={!input.binId || isUpdate}
                placeholder={t(TranslationKey.selectContainer).singular.label}
                value={input.parentId}
                items={[...validContainerIds]}
                textBuilder={item => containers.get(item || '')?.identifier || ''}
                onSelect={selected => setInput(input => input.withParentId(selected || ''))}
              />
            </Grid>
          ) : null}
        </div>
      </div>
    </div>
  );
}
